// src/utils/projectImages.js
//
// Charge automatiquement les images des collections (au build, via webpack).
// Plus besoin de lister gallery-1.jpg, gallery-2.jpg... ni d'importer cuisine1...cuisine14 :
// il suffit de déposer les photos dans le bon dossier.
//
// ⚠ require.context exige des arguments littéraux (pas de variables) : d'où 3 appels.
// Webpack ne bundle que ce qui est dans ces dossiers, comme avec vos require(`...${path}...`).

const collections = {
  cuisines: require.context("../images/Cuisines", true, /\.(jpe?g|png|webp)$/i),
  dressings: require.context("../images/Dressings", true, /\.(jpe?g|png|webp)$/i),
  international: require.context("../images/ProjetsInter", true, /\.(jpe?g|png|webp)$/i),
};

const toUrl = (ctx, key) => {
  const mod = ctx(key);
  return mod && mod.default ? mod.default : mod;
};

// "gallery-12.jpg" -> 12 (tri numérique : 2 avant 10), sinon null
const galleryNumber = (name) => {
  const m = name.match(/^gallery-(\d+)\./i);
  return m ? parseInt(m[1], 10) : null;
};

const cache = new Map();

/**
 * Images d'un projet.
 *
 * @param {"cuisines"|"dressings"|"international"} collection
 * @param {string} project  ex: "project1"
 * @param {string[]} [customGallery]  (optionnel) ordre manuel, ex: ["gallery-1.jpg", "gallery-9.jpg", ...]
 *
 * Par défaut la galerie = gallery-1, gallery-2, ... (tri numérique) puis couverture, L et M
 * (c'est l'ordre que vous aviez dans CollectionsData.js pour les projets "classiques").
 *
 * @returns {{ cover?: string, left?: string, right?: string, gallery: string[] }}
 */
export function getProjectImages(collection, project, customGallery) {
  const cacheKey = `${collection}/${project}/${customGallery ? customGallery.join("|") : ""}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const ctx = collections[collection];
  if (!ctx) throw new Error(`getProjectImages : collection inconnue "${collection}"`);

  // Le "/" final évite la confusion project1 / project10.
  const prefix = `./${project}/`;
  const files = ctx
    .keys()
    .filter((k) => k.startsWith(prefix))
    .map((k) => ({ key: k, name: k.slice(prefix.length) }))
    .filter((f) => !f.name.includes("/")); // pas de sous-dossiers

  const findByName = (name) =>
    files.find((f) => f.name === name) ||
    files.find((f) => f.name.toLowerCase() === name.toLowerCase());

  const cover = files.find((f) =>
    f.name.toLowerCase().startsWith(`${project}-couverture.`.toLowerCase())
  );
  const left = files.find((f) => /^L\./i.test(f.name));
  const right = files.find((f) => /^M\./i.test(f.name));

  let galleryFiles;
  if (customGallery && customGallery.length) {
    galleryFiles = customGallery.map((name) => {
      const f = findByName(name);
      if (!f && process.env.NODE_ENV !== "production") {
        console.warn(`[projectImages] ${collection}/${project} : "${name}" introuvable`);
      }
      return f;
    });
  } else {
    const numbered = files
      .filter((f) => galleryNumber(f.name) !== null)
      .sort((a, b) => galleryNumber(a.name) - galleryNumber(b.name));
    galleryFiles = [...numbered, cover, left, right];
  }

  const result = {
    cover: cover && toUrl(ctx, cover.key),
    left: left && toUrl(ctx, left.key),
    right: right && toUrl(ctx, right.key),
    gallery: galleryFiles.filter(Boolean).map((f) => toUrl(ctx, f.key)),
  };

  cache.set(cacheKey, result);
  return result;
}

/**
 * Image d'une carte de liste : lit <projet>/<projet>-bottom.jpg dans le dossier du projet
 * (ex: images/Cuisines/project1/project1-bottom.jpg).
 * Si ce fichier n'existe pas, on retombe sur <projet>-couverture.jpg.
 */
export function getListCover(collection, project) {
  const ctx = collections[collection];
  if (!ctx) throw new Error(`getListCover : collection inconnue "${collection}"`);
  const find = (suffix) => {
    const re = new RegExp(`^\\./${project}/${project}-${suffix}\\.(jpe?g|png|webp)$`, "i");
    return ctx.keys().find((k) => re.test(k));
  };
  const key = find("bottom") || find("couverture");
  return key ? toUrl(ctx, key) : undefined;
}
